import apiClient from './client';
import { ChemFile, Molecule, SearchResult, MergeRequest, MergeStatus, Project } from '../types/chemistry';

export const chemistryApi = {
  // File operations
  getFiles: async (projectId?: number, category?: string): Promise<ChemFile[]> => {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', String(projectId));
    if (category) params.set('category', category);
    const qs = params.toString();
    const url = qs ? `/files?${qs}` : '/files';
    const response = await apiClient.get<ChemFile[]>(url);
    return response.data;
  },

  getFile: async (id: string): Promise<ChemFile> => {
    const response = await apiClient.get<ChemFile>(`/files/${id}`);
    return response.data;
  },

  uploadFile: async (file: File, onProgress?: (progress: number) => void, projectId?: number): Promise<ChemFile> => {
    const formData = new FormData();
    formData.append('file', file);
    if (projectId) {
      formData.append('project_id', projectId.toString());
    }

    const response = await apiClient.post<ChemFile>('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  },

  deleteFiles: async (ids: string[]): Promise<{ deleted: number; preservedReferencedMolecules: number }> => {
    const response = await apiClient.delete<{ deleted: number; preserved_referenced_molecules?: number }>('/files', { data: { ids } });
    return {
      deleted: response.data.deleted,
      preservedReferencedMolecules: response.data.preserved_referenced_molecules ?? 0,
    };
  },

  // Delete molecules from a file
  deleteMolecules: async (fileId: string, moleculeIds: number[]): Promise<{ deletedCount: number }> => {
    const response = await apiClient.delete<{ status: string; deleted_count?: number }>(`/index/${fileId}/molecules`, {
      data: { moleculesRequest: { moleculeIds } }
    });
    return { deletedCount: response.data.deleted_count ?? moleculeIds.length };
  },

  // Duplicate molecules in a file
  duplicateMolecules: async (fileId: string, moleculeIds: number[]): Promise<{ duplicatedCount: number }> => {
    const response = await apiClient.post<{ duplicated_count: number; new_ids: number[] }>(
      `/index/${fileId}/molecules/duplicate`,
      { molecule_ids: moleculeIds }
    );
    return { duplicatedCount: response.data.duplicated_count };
  },

  // Bulk edit molecules
  bulkEditMolecules: async (
    fileId: string,
    moleculeIds: number[],
    property: string,
    value: string | number | null
  ): Promise<{ updatedCount: number }> => {
    const response = await apiClient.patch<{ updated_count: number }>(
      `/index/${fileId}/molecules/bulk-edit`,
      { molecule_ids: moleculeIds, property, value }
    );
    return { updatedCount: response.data.updated_count };
  },

  renameFile: async (fileId: string, newName: string): Promise<void> => {
    await apiClient.patch(`/files/${fileId}/rename`, { name: newName });
  },

  downloadFile: async (id: string): Promise<Blob> => {
    const response = await apiClient.get(`/files/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Molecule operations - uses same query endpoint as SSRM
  getMolecules: async (
    fileId: string,
    page: number = 1,
    limit: number = 100
  ): Promise<{ molecules: Molecule[]; total: number }> => {
    const startRow = (page - 1) * limit;
    const endRow = startRow + limit;
    console.log('[API] getMolecules called:', { fileId, page, limit, startRow, endRow });

    // Use the same query endpoint that SSRM uses - it actually returns molecules
    const response = await apiClient.post<{
      rows: Array<{
        id: string;
        mol_index: number | null;
        smiles: string;
        molfile: string;
        Structure: string;
        mw: number;
        logp: number;
        tpsa: number;
        hbd: number;
        hba: number;
        rotatable_bonds: number;
        ring_count: number;
        properties: Record<string, any>;
      }>;
      lastRow: number;
    }>(`/files/${fileId}/molecules/query`, {
      startRow,
      endRow,
    });

    console.log('[API] getMolecules response:', { rowCount: response.data.rows?.length, lastRow: response.data.lastRow });

    // Map to frontend Molecule type (same as queryMolecules)
    const molecules = (response.data.rows || []).map((row, index) => {
      const properties: Record<string, any> = { ...row.properties };

      // Map chemistry properties to display-friendly names
      if (row.mw != null) properties['MW'] = Math.round(row.mw * 100) / 100;
      if (row.logp != null) properties['LogP'] = Math.round(row.logp * 100) / 100;
      if (row.tpsa != null) properties['TPSA'] = Math.round(row.tpsa * 100) / 100;
      if (row.hbd != null) properties['HBD'] = row.hbd;
      if (row.hba != null) properties['HBA'] = row.hba;
      if (row.rotatable_bonds != null) properties['RotBonds'] = row.rotatable_bonds;
      if (row.ring_count != null) properties['Rings'] = row.ring_count;

      return {
        ...row,
        id: parseInt(row.id, 10),
        mol_index: row.mol_index ?? startRow + index,
        molfile: row.molfile || row.Structure || '',
        smiles: row.smiles || '',
        properties,
      };
    });

    console.log('[API] getMolecules returning:', { count: molecules.length, total: response.data.lastRow });
    return { molecules, total: response.data.lastRow };
  },

  // Structure search
  searchStructure: async (
    fileId: string,
    molfile: string,
    searchType: 'substructure' | 'similarity' | 'exact',
    params?: {
      threshold?: number;
      stereoMode?: 'strict' | 'ignore';
      tautomerMode?: 'exact' | 'inchi' | 'rsmarts';
      resonance?: boolean;
    }
  ): Promise<SearchResult> => {
    // Build request in format backend expects
    const requestBody: Record<string, any> = {
      fileIds: [fileId],
      queryStructure: molfile,
      searchType: searchType,
      limit: 1000,  // Get all matches
    };

    // Add similarity threshold if provided
    if (searchType === 'similarity' && params?.threshold !== undefined) {
      requestBody.similarity = { threshold: params.threshold };
    }

    // Add chemistry options
    if (params?.stereoMode) {
      requestBody.stereoMode = params.stereoMode;
    }
    if (params?.tautomerMode) {
      requestBody.tautomerMode = params.tautomerMode;
    }
    if (params?.resonance !== undefined) {
      requestBody.resonance = params.resonance;
    }

    console.log('[chemistryApi] searchStructure request:', { fileId, searchType, molfileLen: molfile?.length });
    console.log('[chemistryApi] requestBody:', JSON.stringify(requestBody).substring(0, 500));

    const response = await apiClient.post<{
      molecules: Array<{
        id: string;
        structure: string;
        molproperties: Record<string, any>;
        customOrder: number;
      }>;
      stat: { filtered: number; matched: number; inprogress: boolean; error?: string };
      searchId: string;
    }>('/index/search', requestBody);

    console.log('[chemistryApi] searchStructure response stat:', response.data.stat);
    console.log('[chemistryApi] searchStructure molecules count:', response.data.molecules?.length);

    // Check for error in response
    if (response.data.stat?.error) {
      console.error('[chemistryApi] searchStructure error from backend:', response.data.stat.error);
      throw new Error(response.data.stat.error);
    }

    // Transform response to frontend SearchResult format
    const molecules = response.data.molecules.map((mol, index) => ({
      id: parseInt(mol.id, 10),
      mol_index: mol.customOrder || index + 1,
      molfile: mol.structure,
      smiles: '',  // Backend doesn't return separate smiles
      properties: mol.molproperties || {},
    }));

    const result = {
      molecules,
      total: response.data.stat.matched,
      searchContext: `${searchType} search`,
    };

    console.log('[chemistryApi] searchStructure final result:', { moleculesCount: result.molecules.length, total: result.total });

    return result;
  },

  // Merge operations
  mergeFiles: async (request: MergeRequest): Promise<{ taskId: string }> => {
    const response = await apiClient.post<{ taskId: string }>('/merge', request);
    return response.data;
  },

  getMergeStatus: async (taskId: string): Promise<MergeStatus> => {
    const response = await apiClient.get<MergeStatus>(`/merge/${taskId}`);
    return response.data;
  },

  cancelMerge: async (taskId: string): Promise<{ success: boolean; status: string; message: string }> => {
    const response = await apiClient.post<{ success: boolean; status: string; message: string }>(`/merge/${taskId}/cancel`);
    return response.data;
  },

  cancelUpload: async (fileId: string): Promise<{ success: boolean; status: string; message: string }> => {
    const response = await apiClient.post<{ success: boolean; status: string; message: string }>(`/files/${fileId}/cancel`);
    return response.data;
  },

  // Get files currently being processed (locked)
  getLockedFiles: async (): Promise<string[]> => {
    const response = await apiClient.get<{ lockedFileIds: string[] }>('/tasks/locked-files');
    return response.data.lockedFileIds;
  },

  // Deduplication
  deduplicateFile: async (fileId: string): Promise<{ taskId: string }> => {
    const response = await apiClient.post<{ taskId: string }>(`/index/${fileId}/molecules/deduplicate`);
    return response.data;
  },

  // Clear search context on backend (prevents stale results)
  clearSearchContext: async (fileId?: string): Promise<void> => {
    if (fileId) {
      await apiClient.delete(`/search/context/${fileId}`);
    } else {
      await apiClient.delete('/search/context');
    }
  },

  // Add calculated column
  addCalculatedColumn: async (
    fileId: string,
    columnType: string,
    columnName?: string
  ): Promise<{
    success: boolean;
    columnName: string;
    columnType: string;
    updatedCount: number;
    errorCount: number;
    totalMolecules: number;
  }> => {
    const response = await apiClient.post(`/index/${fileId}/calculated-column`, {
      column_type: columnType,
      column_name: columnName,
    });
    return response.data;
  },

  // Add new molecule (row) to a file
  addMolecule: async (
    fileId: string,
    molecule: {
      structure: string;  // SMILES or molfile
      molproperties?: Record<string, any>;
    }
  ): Promise<{
    id: string;
    structure: string;
    molproperties: Record<string, any>;
  }[]> => {
    const response = await apiClient.post(`/index/${fileId}/molecule`, [molecule]);
    return response.data;
  },

  // Add custom column to all molecules in a file
  addCustomColumn: async (
    fileId: string,
    columnName: string,
    defaultValue: string | number | null = null
  ): Promise<{ success: boolean; updatedCount: number }> => {
    // This updates all molecules to add the new property
    const response = await apiClient.post(`/index/${fileId}/custom-column`, {
      column_name: columnName,
      default_value: defaultValue,
    });
    return response.data;
  },

  // Update molecule (for inline editing)
  updateMolecule: async (
    fileId: string,
    moleculeId: number,
    data: { structure?: string; properties?: Record<string, any> }
  ): Promise<void> => {
    // Backend expects: { id, structure?, molproperties? }
    await apiClient.put(`/index/${fileId}/molecule`, {
      id: moleculeId,
      structure: data.structure,
      molproperties: data.properties,
    });
  },

  // Server-Side Row Model query for AG Grid
  queryMolecules: async (
    fileId: string,
    params: {
      startRow: number;
      endRow: number;
      sortModel?: Array<{ colId: string; sort: string }>;
      filterModel?: Record<string, any>;
      matchIds?: number[];  // Filter to specific molecule IDs (from search results)
    }
  ): Promise<{ rows: Molecule[]; lastRow: number }> => {
    const response = await apiClient.post<{
      rows: Array<{
        id: string;
        mol_index: number | null;
        smiles: string;
        molfile: string;
        Structure: string;
        inchi_key: string | null;
        mw: number;
        logp: number;
        tpsa: number;
        hbd: number;
        hba: number;
        rotatable_bonds: number;
        ring_count: number;
        ip_status: string | null;
        patent_count_exact: number | null;
        patent_count_similar: number | null;
        patent_max_zone: number | null;
        target_count: number | null;
        pathway_count: number | null;
        reaction_count: number | null;
        enriched_at: string | null;
        properties: Record<string, any>;
      }>;
      lastRow: number;
    }>(`/files/${fileId}/molecules/query`, params);

    // Transform to frontend Molecule type
    const molecules = response.data.rows.map((row, index) => {
      const properties: Record<string, any> = { ...row.properties };

      // Map chemistry properties to display-friendly names
      if (row.mw != null) properties['MW'] = Math.round(row.mw * 100) / 100;
      if (row.logp != null) properties['LogP'] = Math.round(row.logp * 100) / 100;
      if (row.tpsa != null) properties['TPSA'] = Math.round(row.tpsa * 100) / 100;
      if (row.hbd != null) properties['HBD'] = row.hbd;
      if (row.hba != null) properties['HBA'] = row.hba;
      if (row.rotatable_bonds != null) properties['RotBonds'] = row.rotatable_bonds;
      if (row.ring_count != null) properties['Rings'] = row.ring_count;

      return {
        id: parseInt(row.id, 10),
        mol_index: row.mol_index ?? params.startRow + index,
        molfile: row.molfile || row.Structure || '',
        smiles: row.smiles || '',
        properties,
        ip_status: row.ip_status || null,
        patent_count_exact: row.patent_count_exact ?? null,
        patent_count_similar: row.patent_count_similar ?? null,
        patent_max_zone: row.patent_max_zone ?? null,
        target_count: row.target_count ?? null,
        pathway_count: row.pathway_count ?? null,
        reaction_count: row.reaction_count ?? null,
        enriched_at: row.enriched_at ?? null,
      };
    });

    return { rows: molecules, lastRow: response.data.lastRow };
  },

  // Download operations
  startDownload: async (fileId: string, fileName?: string): Promise<{
    taskId: string;
    status: string;
    percent: number;
    totalMolecules?: number;
  }> => {
    const response = await apiClient.post('/index/download', {
      fileId,
      fileName: fileName || 'export.sdf',
    });
    return response.data;
  },

  getDownloadStatus: async (taskId: string): Promise<{
    taskId: string;
    status: string;
    percent: number;
    stage?: string;
    downloadUrl?: string;
    totalMolecules?: number;
  }> => {
    const response = await apiClient.get(`/index/download/status/${taskId}`);
    return response.data;
  },

  cancelDownload: async (taskId: string): Promise<{ status: string }> => {
    const response = await apiClient.post(`/index/download/${taskId}/cancel`);
    return response.data;
  },

  // Project operations
  getProjects: async (): Promise<Project[]> => {
    const response = await apiClient.get<Project[]>('/projects');
    return response.data;
  },

  getProject: async (id: number): Promise<Project> => {
    const response = await apiClient.get<Project>(`/projects/${id}`);
    return response.data;
  },

  getProjectTargets: async (projectId: number): Promise<{ targets: any[]; total: number }> => {
    const response = await apiClient.get(`/projects/${projectId}/targets`);
    return response.data;
  },

  createProject: async (data: { name: string; description?: string; color?: string }): Promise<Project> => {
    const response = await apiClient.post<Project>('/projects', data);
    return response.data;
  },

  updateProject: async (id: number, data: { name?: string; description?: string; color?: string }): Promise<Project> => {
    const response = await apiClient.put<Project>(`/projects/${id}`, data);
    return response.data;
  },

  deleteProject: async (id: number): Promise<void> => {
    await apiClient.delete(`/projects/${id}`);
  },

  archiveProject: async (id: number): Promise<void> => {
    await apiClient.put(`/projects/${id}/archive`);
  },

  restoreProject: async (id: number): Promise<void> => {
    await apiClient.patch(`/projects/${id}/restore`);
  },

  getArchivedProjects: async (): Promise<Project[]> => {
    const response = await apiClient.get<Project[]>('/projects?archived_only=true');
    return response.data;
  },

  moveFile: async (fileId: string, projectId: number): Promise<void> => {
    await apiClient.put(`/files/${fileId}/move`, { projectId });
  },

  bulkMoveFiles: async (fileIds: string[], projectId: number): Promise<{ movedCount: number }> => {
    const response = await apiClient.put<{ movedCount: number }>('/files/bulk-move', {
      file_ids: fileIds.map(id => parseInt(id, 10)),
      project_id: projectId,
    });
    return response.data;
  },

  // Get molecule previews for project cards
  getProjectPreviewMolecules: async (projectId: number, limit: number = 4): Promise<{
    previews: Array<{ molecule_id: number; svg: string }>;
  }> => {
    const response = await apiClient.get<{
      previews: Array<{ molecule_id: number; svg: string }>;
    }>(`/projects/${projectId}/preview-molecules?limit=${limit}`);
    return response.data;
  },

  // Project-wide compound queries
  queryProjectCompounds: async (
    projectId: number,
    params: {
      startRow: number;
      endRow: number;
      sortModel?: Array<{ colId: string; sort: string }>;
      filterModel?: Record<string, any>;
    }
  ): Promise<{ rows: Molecule[]; lastRow: number }> => {
    const response = await apiClient.post<{
      rows: Array<{
        id: string;
        mol_index: number | null;
        smiles: string;
        molfile: string;
        Structure: string;
        inchi_key: string | null;
        mw: number;
        logp: number;
        tpsa: number;
        hbd: number;
        hba: number;
        rotatable_bonds: number;
        ring_count: number;
        ip_status: string | null;
        patent_count_exact: number | null;
        patent_count_similar: number | null;
        patent_max_zone: number | null;
        target_count: number | null;
        pathway_count: number | null;
        reaction_count: number | null;
        enriched_at: string | null;
        properties: Record<string, any>;
        source_file: string | null;
        source_file_id: string | null;
      }>;
      lastRow: number;
    }>(`/projects/${projectId}/compounds/query`, params);

    const molecules = response.data.rows.map((row, index) => {
      const properties: Record<string, any> = { ...row.properties };
      if (row.mw != null) properties['MW'] = Math.round(row.mw * 100) / 100;
      if (row.logp != null) properties['LogP'] = Math.round(row.logp * 100) / 100;
      if (row.tpsa != null) properties['TPSA'] = Math.round(row.tpsa * 100) / 100;
      if (row.hbd != null) properties['HBD'] = row.hbd;
      if (row.hba != null) properties['HBA'] = row.hba;
      if (row.rotatable_bonds != null) properties['RotBonds'] = row.rotatable_bonds;
      if (row.ring_count != null) properties['Rings'] = row.ring_count;
      if (row.source_file) properties['Source File'] = row.source_file.replace(/^[a-f0-9]{8}_/i, '');

      return {
        id: parseInt(row.id, 10),
        mol_index: row.mol_index ?? params.startRow + index,
        molfile: row.molfile || row.Structure || '',
        smiles: row.smiles || '',
        properties,
        ip_status: row.ip_status || null,
        patent_count_exact: row.patent_count_exact ?? null,
        patent_count_similar: row.patent_count_similar ?? null,
        patent_max_zone: row.patent_max_zone ?? null,
        target_count: row.target_count ?? null,
        pathway_count: row.pathway_count ?? null,
        reaction_count: row.reaction_count ?? null,
        enriched_at: row.enriched_at ?? null,
      };
    });

    return { rows: molecules, lastRow: response.data.lastRow };
  },

  getProjectDuplicates: async (projectId: number): Promise<{
    duplicateCount: number;
    groups: Array<{ inchiKey: string; count: number; fileIds: string[] }>;
  }> => {
    const response = await apiClient.get(`/projects/${projectId}/compounds/duplicates`);
    return response.data;
  },

  // Reactions API
  getReactions: async (options: {
    fileId?: number;
    limit?: number;
    offset?: number;
  } = {}): Promise<{
    reactions: Array<{
      id: number;
      file_id: number;
      rxn_index: number;
      reaction_smiles: string;
      properties: Record<string, any>;
      created_at: string;
      participants?: Array<{
        id: number;
        role: string;
        smiles: string | null;
        inchi_key: string | null;
      }>;
    }>;
    total: number;
    limit: number;
    offset: number;
  }> => {
    const { fileId, limit = 100, offset = 0 } = options;
    const params: Record<string, any> = { limit, offset };
    if (fileId) params.file_id = fileId;
    const response = await apiClient.get('/reactions', { params });
    return response.data;
  },

  getReaction: async (reactionId: number): Promise<{
    id: number;
    file_id: number;
    rxn_index: number;
    rxnfile: string;
    reaction_smiles: string;
    properties: Record<string, any>;
    created_at: string;
    participants: Array<{
      id: number;
      role: string;
      stoichiometry: number;
      molecule_id: number | null;
    }>;
  }> => {
    const response = await apiClient.get(`/reactions/${reactionId}`);
    return response.data;
  },

  getReactionSvg: async (reactionId: number, width: number = 400, height: number = 200): Promise<{
    svg: string;
  }> => {
    const response = await apiClient.get(`/reactions/${reactionId}/svg`, {
      params: { width, height }
    });
    return response.data;
  },

  getReactionCount: async (fileId: number): Promise<{
    file_id: number;
    count: number;
  }> => {
    const response = await apiClient.get(`/reactions/file/${fileId}/count`);
    return response.data;
  },

  searchReactions: async (
    query: string,
    searchType: 'substructure' | 'exact' | 'smarts' = 'substructure',
    fileId?: number,
    limit: number = 100
  ): Promise<{
    results: Array<{
      id: number;
      file_id: number;
      rxn_index: number;
      reaction_smiles: string;
      properties: Record<string, any>;
      file_name: string;
    }>;
    count: number;
    search_type: string;
  }> => {
    const response = await apiClient.post('/reactions/search', null, {
      params: { query, search_type: searchType, file_id: fileId, limit }
    });
    return response.data;
  },

  updateReaction: async (
    reactionId: number,
    data: { rxnfile?: string; reaction_smiles?: string }
  ): Promise<{
    id: number;
    file_id: number;
    rxn_index: number;
    rxnfile: string;
    reaction_smiles: string;
    properties: Record<string, any>;
    created_at: string;
    participants: Array<{
      id: number;
      role: string;
      stoichiometry: number;
      molecule_id: number | null;
    }>;
  }> => {
    const response = await apiClient.put(`/reactions/${reactionId}`, data);
    return response.data;
  },

  // Biologics API
  getBiologics: async (fileId?: number, limit: number = 100, offset: number = 0): Promise<{
    biologics: Array<{
      id: number;
      file_id: number | null;
      name: string | null;
      sequence: string;
      sequence_type: string | null;
      organism: string | null;
      uniprot_id: string | null;
      molecular_weight: number | null;
      properties: Record<string, any>;
    }>;
    total: number;
  }> => {
    const params: any = { limit, offset };
    if (fileId !== undefined) {
      params.file_id = fileId;
    }
    const response = await apiClient.get('/biologics', { params });
    return response.data;
  },

  getBiologic: async (biologicId: number): Promise<{
    id: number;
    file_id: number | null;
    name: string | null;
    sequence: string;
    sequence_type: string | null;
    organism: string | null;
    uniprot_id: string | null;
    molecular_weight: number | null;
    properties: Record<string, any>;
  }> => {
    const response = await apiClient.get(`/biologics/${biologicId}`);
    return response.data;
  },

  getBiologicCount: async (fileId: number): Promise<{
    file_id: number;
    count: number;
  }> => {
    const response = await apiClient.get(`/biologics/file/${fileId}/count`);
    return response.data;
  },

  // PDB structure fetching
  fetchPdb: async (pdbId: string): Promise<{
    pdb_id: string;
    pdb_content: string;
  }> => {
    const response = await apiClient.get(`/biologics/pdb/${pdbId}`);
    return response.data;
  },

  fetchAlphaFold: async (uniprotId: string): Promise<{
    uniprot_id: string;
    pdb_content: string;
    source: string;
  }> => {
    const response = await apiClient.get(`/biologics/alphafold/${uniprotId}`);
    return response.data;
  },

  // Biologic-Reaction linking
  getBiologicReactions: async (biologicId: number): Promise<{
    biologic_id: number;
    reactions: Array<{
      id: number;
      reaction_id: number;
      role: string;
      reaction_smiles: string | null;
      file_id: number;
      file_name: string | null;
    }>;
    total: number;
  }> => {
    const response = await apiClient.get(`/biologics/${biologicId}/reactions`);
    return response.data;
  },

  linkBiologicToReactions: async (
    biologicId: number,
    reactionIds: number[],
    role: string = 'catalyst'
  ): Promise<{
    biologic_id: number;
    reactions: Array<{
      id: number;
      reaction_id: number;
      role: string;
      reaction_smiles: string | null;
      file_id: number;
      file_name: string | null;
    }>;
    total: number;
  }> => {
    const response = await apiClient.post(`/biologics/${biologicId}/reactions`, {
      reaction_ids: reactionIds,
      role
    });
    return response.data;
  },

  unlinkBiologicFromReaction: async (biologicId: number, reactionId: number): Promise<void> => {
    await apiClient.delete(`/biologics/${biologicId}/reactions/${reactionId}`);
  },

  getReactionBiologics: async (reactionId: number): Promise<Array<{
    id: number;
    biologic_id: number;
    role: string;
    name: string | null;
    uniprot_id: string | null;
    sequence_type: string | null;
  }>> => {
    const response = await apiClient.get(`/reactions/${reactionId}/biologics`);
    return response.data;
  },

  // Molecule operations
  getMolecule: async (moleculeId: number, locateInFile = false): Promise<{
    id: number;
    file_id: number;
    smiles: string | null;
    svg: string | null;
    mw: number | null;
    logp: number | null;
    tpsa: number | null;
    hbd: number | null;
    hba: number | null;
    rotatable_bonds: number | null;
    ring_count: number | null;
    properties: Record<string, any>;
    file_row_index?: number | null;
  }> => {
    const response = await apiClient.get(`/molecules/${moleculeId}`, {
      params: locateInFile ? { include_file_position: true } : undefined,
    });
    return response.data;
  },

  getMoleculeSvg: async (moleculeId: number, width: number = 200, height: number = 200): Promise<{
    svg: string;
  }> => {
    const response = await apiClient.get(`/molecules/${moleculeId}/svg`, {
      params: { width, height }
    });
    return response.data;
  },

  getMoleculeFiles: async (moleculeId: number): Promise<{
    molecule_id: number;
    smiles: string;
    files: Array<{
      file_id: number;
      file_name: string;
      file_type: string;
      molecule_count: number;
    }>;
    total: number;
  }> => {
    const response = await apiClient.get(`/molecules/${moleculeId}/files`);
    return response.data;
  },

  // Molecule-Target linking
  getMoleculeTargets: async (moleculeId: number): Promise<{
    molecule_id: number;
    targets: Array<{
      id: number;
      biologic_id: number;
      interaction_type: string;
      affinity_value: number | null;
      affinity_unit: string | null;
      target_name: string | null;
      uniprot_id: string | null;
      sequence_type: string | null;
    }>;
    total: number;
  }> => {
    const response = await apiClient.get(`/molecules/${moleculeId}/targets`);
    return response.data;
  },

  linkMoleculeToTarget: async (
    moleculeId: number,
    biologicId: number,
    interactionType: string = 'Unknown',
    affinityValue?: number,
    affinityUnit?: string,
    bindingSite?: string,
    species?: string,
    relationshipType?: string
  ): Promise<{
    molecule_id: number;
    targets: Array<{
      id: number;
      biologic_id: number;
      interaction_type: string;
      affinity_value: number | null;
      affinity_unit: string | null;
      binding_site: string | null;
      species: string | null;
      relationship_type: string | null;
      target_name: string | null;
      uniprot_id: string | null;
      sequence_type: string | null;
    }>;
    total: number;
  }> => {
    const response = await apiClient.post(`/molecules/${moleculeId}/targets`, {
      biologic_id: biologicId,
      interaction_type: interactionType,
      affinity_value: affinityValue,
      affinity_unit: affinityUnit,
      binding_site: bindingSite,
      species: species,
      relationship_type: relationshipType,
    });
    return response.data;
  },

  unlinkMoleculeFromTarget: async (moleculeId: number, biologicId: number): Promise<void> => {
    await apiClient.delete(`/molecules/${moleculeId}/targets/${biologicId}`);
  },

  getBiologicLigands: async (biologicId: number): Promise<{
    biologic_id: number;
    ligands: Array<{
      id: number;
      molecule_id: number;
      interaction_type: string;
      affinity_value: number | null;
      affinity_unit: string | null;
      smiles: string | null;
      file_id: number | null;
      name?: string | null;
      reference_id?: string | null;
      file_name?: string | null;
      binding_site?: string | null;
    }>;
    total: number;
  }> => {
    const response = await apiClient.get(`/biologics/${biologicId}/ligands`);
    return response.data;
  },

  // Patent enrichment
  startPatentEnrichment: async (fileId: string): Promise<{
    task_id: string;
    status: string;
    progress: number;
    total: number;
    phase: string;
    found_count: number;
  }> => {
    const numericId = parseInt(fileId, 10);
    if (isNaN(numericId)) throw new Error('Invalid file ID for patent enrichment');
    const response = await apiClient.post('/enrich/patents', { file_id: numericId });
    return response.data;
  },

  getPatentEnrichmentStatus: async (taskId: string): Promise<{
    task_id: string;
    status: string;
    progress: number;
    total: number;
    phase: string;
    found_count: number;
    error?: string;
  }> => {
    const response = await apiClient.get(`/enrich/status/${taskId}`);
    return response.data;
  },

  // Patent Collision Map
  getPatentClaims: async (): Promise<any[]> => {
    const response = await apiClient.get('/patents/claims');
    return response.data;
  },

  getCollisionMap: async (fileId: string, projectId?: number): Promise<{
    file_id: number;
    patents: any[];
    molecules: any[];
    summary: {
      total_molecules: number;
      enriched_molecules: number;
      pending_molecules: number;
      zone_counts: Record<string, number>;
      total_patents: number;
    };
    zone_colors: Record<number, string>;
    zone_labels: Record<number, string>;
    jurisdictions?: Record<string, number>;
  }> => {
    const params = projectId != null ? { project_id: projectId } : undefined;
    const response = await apiClient.get(`/patents/collision-map/${fileId}`, { params });
    return response.data;
  },

  generateCollisionMap: async (
    fileId: string,
    options?: { patent_claim_ids?: number[]; atom_threshold?: number; mcs_threshold?: number }
  ): Promise<{ status: string; file_id: number; molecule_count: number; patent_count: number }> => {
    const numericId = parseInt(fileId, 10);
    if (isNaN(numericId)) throw new Error('Invalid file ID for collision map');
    const response = await apiClient.post('/patents/collision-map/generate', {
      file_id: numericId,
      ...options,
    });
    return response.data;
  },

  getIsosteres: async (): Promise<any[]> => {
    const response = await apiClient.get('/patents/isosteres');
    return response.data;
  },

  // Async patent scanning
  startPatentScan: async (fileId: number): Promise<{ scan_id: number; status: string; total_molecules: number }> => {
    const response = await apiClient.post(`/patents/scan/${fileId}`);
    return response.data;
  },

  getPatentScanStatus: async (fileId: number): Promise<{
    scan_id?: number;
    status: string;
    progress?: number;
    total?: number;
    scanned?: number;
    hits?: number;
    error?: string;
    message?: string;
  }> => {
    const response = await apiClient.get(`/patents/scan/${fileId}/status`);
    return response.data;
  },

  getPatentScanResults: async (fileId: number): Promise<any> => {
    const response = await apiClient.get(`/patents/scan/${fileId}/results`);
    return response.data;
  },

  batchSimilarityCompare: async (
    pairs: { mol_smiles: string; patent_smiles?: string | null; surechembl_id?: string }[],
    mode: 'mcs' | 'substructure'
  ): Promise<{ results: any[]; mode: string }> => {
    const response = await apiClient.post('/patents/similarity-batch', { pairs, mode });
    return response.data;
  },

  // Tenant features
  getFeatures: async (): Promise<Record<string, boolean>> => {
    const response = await apiClient.get('/v1/tenants/features');
    return response.data;
  },

  // Intelligence enrichment (batch)
  startIntelligence: async (fileId: string, mode: 'fast' = 'fast'): Promise<{
    task_id: string;
    status: string;
    progress: number;
    total: number;
    skipped: number;
    summary?: { message?: string; targets?: number; pathways?: number; reactions?: number; total_hits?: number };
  }> => {
    const numericId = parseInt(fileId, 10);
    if (isNaN(numericId)) throw new Error('Invalid file ID for enrichment');
    const response = await apiClient.post(`/enrich/intelligence/${numericId}?mode=${mode}`);
    return response.data;
  },

  getIntelligenceStatus: async (fileId: string, taskId: string): Promise<{
    task_id: string;
    status: string;
    progress: number;
    total: number;
    skipped: number;
    error?: string;
    summary?: { message?: string; targets?: number; pathways?: number; reactions?: number; total_hits?: number };
  }> => {
    const numericId = parseInt(fileId, 10);
    if (isNaN(numericId)) throw new Error('Invalid file ID for enrichment');
    const response = await apiClient.get(`/enrich/intelligence/${numericId}/status/${taskId}`);
    return response.data;
  },
  getEnrichmentInfo: async (fileId: string): Promise<{
    total: number;
    fast_enriched: number;
    patent_enriched: number;
    fast_last_run: string | null;
    patent_last_run: string | null;
  }> => {
    const numericId = parseInt(fileId, 10);
    if (isNaN(numericId)) throw new Error('Invalid file ID for enrichment');
    const response = await apiClient.get(`/enrich/intelligence/${numericId}/enrichment-info`);
    return response.data;
  },

  // Entity type classification
  classifyEntity: async (data: { smiles?: string; sequence?: string; helm?: string; name?: string }): Promise<{
    entity_type: string;
    confidence: number;
    reason: string;
  }> => {
    const response = await apiClient.post('/molecules/classify', data);
    return response.data;
  },

  updateEntityType: async (moleculeId: number, entityType: string, reason?: string): Promise<{
    success: boolean;
    old_type: string;
    new_type: string;
  }> => {
    const response = await apiClient.patch(`/molecules/${moleculeId}/entity-type`, {
      entity_type: entityType,
      reason,
    });
    return response.data;
  },

  backfillEntityTypes: async (): Promise<{
    total_processed: number;
    reclassified: Record<string, number>;
    unchanged: number;
  }> => {
    const response = await apiClient.post('/molecules/admin/backfill-entity-types');
    return response.data;
  },

  getEntityTypeCounts: async (fileId: string): Promise<{ entity_types: Record<string, number> }> => {
    const numericId = parseInt(fileId, 10);
    if (isNaN(numericId)) return { entity_types: {} };
    const response = await apiClient.get(`/files/${numericId}/entity-type-counts`);
    return response.data;
  },
};

export default chemistryApi;
